import React, { useState, useEffect } from "react";
import { getAudiences, getForecast, getCampaignOptimisation, getAttribution, genAdCopy } from "../../../api";
import { Megaphone, Loader2, Users, LineChart, PoundSterling, Target } from "lucide-react";

const money = (n) => (n || n === 0 ? `£${Number(n).toLocaleString()}` : "—");

const AdminGrowthIntel = () => {
  const [data, setData] = useState({ audiences: [], forecast: null, optimisation: null, attribution: null });
  const [loading, setLoading] = useState(true);
  const [adCopy, setAdCopy] = useState(null);
  const [gen, setGen] = useState(null);

  useEffect(() => {
    (async () => {
      const [a, f, o, at] = await Promise.all([getAudiences(), getForecast(), getCampaignOptimisation(), getAttribution()]);
      setData({ audiences: a.segments || [], forecast: f, optimisation: o, attribution: at });
      setLoading(false);
    })();
  }, []);

  const makeAd = async (seg) => {
    setGen(seg.key); setAdCopy(null);
    try { const r = await genAdCopy({ segment: seg.name, area: "Bath", platform: "meta" }); setAdCopy({ seg: seg.name, copy: r.copy }); } finally { setGen(null); }
  };

  if (loading) return <div className="py-16 grid place-items-center text-[#8a8a8a]"><Loader2 className="animate-spin" /></div>;
  const { audiences, forecast, optimisation, attribution } = data;

  return (
    <div className="space-y-6" data-testid="admin-growth-intel">
      <div>
        <h2 className="text-[22px] font-semibold text-[#2b2b2b] flex items-center gap-2"><Megaphone size={20} className="text-[#b8935f]" /> Marketing & Revenue Intelligence</h2>
        <p className="text-[13px] text-[#8a8a8a] mt-1">Auto-built audiences, AI ad copy, cost-per-instruction, attribution and a 30-day forecast. Benchmarks are estimates.</p>
      </div>

      {/* Forecast */}
      <div className="bg-[#2b2b2b] rounded-2xl p-5 text-white" data-testid="growth-forecast">
        <div className="text-[11px] uppercase tracking-wide text-[#b8935f] flex items-center gap-1.5"><LineChart size={14} /> {forecast.window}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
          {[["Valuations", forecast.expectedValuations], ["Instructions", forecast.expectedInstructions], ["Listings", forecast.expectedListings], ["Commission pipeline", money(forecast.expectedCommissionPipeline)]].map(([l, v]) => (
            <div key={l}><div className="text-[24px] font-bold">{v}</div><div className="text-[12px] text-[#a8a8a8]">{l}</div></div>
          ))}
        </div>
        <div className="text-[13px] text-[#e0d5c4] mt-3">{forecast.recommendation}</div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Audiences */}
        <div className="bg-white border border-[#f0ece5] rounded-xl p-4">
          <h3 className="text-[14px] font-semibold text-[#2b2b2b] mb-3 flex items-center gap-2"><Users size={16} className="text-[#b8935f]" /> Auto-built audiences</h3>
          <div className="space-y-2">
            {audiences.map((s) => (
              <div key={s.key} className="flex items-center gap-3 border border-[#f0ece5] rounded-lg p-2.5" data-testid={`audience-${s.key}`}>
                <div className="flex-1 min-w-0"><div className="text-[13px] font-medium text-[#2b2b2b]">{s.name}</div><div className="text-[12px] text-[#8a8a8a]">{s.size} contacts · {s.channel}</div></div>
                <button onClick={() => makeAd(s)} disabled={gen === s.key} data-testid={`ad-copy-${s.key}`} className="text-[12px] bg-[#faf7f2] hover:bg-[#f0e6d6] text-[#7a5c2e] rounded-lg px-3 py-1.5 disabled:opacity-60">{gen === s.key ? <Loader2 size={13} className="animate-spin" /> : "AI ad copy"}</button>
              </div>
            ))}
          </div>
          {adCopy && (
            <div className="mt-3 bg-[#faf7f2] rounded-lg p-3" data-testid="ad-copy-result">
              <div className="text-[11px] text-[#b8935f] font-medium mb-1">Ad concept · {adCopy.seg}</div>
              <p className="text-[12px] text-[#4a4a4a] whitespace-pre-wrap leading-relaxed">{adCopy.copy}</p>
            </div>
          )}
        </div>

        {/* Optimisation + Attribution */}
        <div className="space-y-4">
          <div className="bg-white border border-[#f0ece5] rounded-xl p-4" data-testid="growth-optimisation">
            <h3 className="text-[14px] font-semibold text-[#2b2b2b] mb-3 flex items-center gap-2"><Target size={16} className="text-[#b8935f]" /> Cost per instruction</h3>
            <table className="w-full text-[13px]">
              <thead><tr className="text-[11px] text-[#8a8a8a] text-left"><th className="py-1">Channel</th><th>Cost/lead</th><th>Instr. rate</th><th className="text-right">Cost/instr.</th></tr></thead>
              <tbody>{optimisation.channels.map((c) => (
                <tr key={c.channel} className="border-t border-[#f5f1ea]"><td className="py-1.5 font-medium text-[#2b2b2b]">{c.channel}</td><td>{money(c.costPerLead)}</td><td>{Math.round(c.instructionRate * 100)}%</td><td className="text-right font-semibold text-[#b8935f]">{money(c.costPerInstruction)}</td></tr>
              ))}</tbody>
            </table>
            <p className="text-[12px] text-[#6a6a6a] mt-2">{optimisation.recommendation}</p>
          </div>
          <div className="bg-white border border-[#f0ece5] rounded-xl p-4" data-testid="growth-attribution">
            <h3 className="text-[14px] font-semibold text-[#2b2b2b] mb-3 flex items-center gap-2"><PoundSterling size={16} className="text-[#b8935f]" /> Revenue attribution</h3>
            <div className="text-[13px] text-[#6a6a6a] mb-2">Est. commission pipeline: <span className="font-semibold text-[#2b2b2b]">{money(attribution.estimatedCommission)}</span></div>
            <div className="space-y-1.5">{attribution.byChannel.map((r) => (
              <div key={r.channel} className="flex items-center gap-2"><span className="text-[12px] w-16 text-[#4a4a4a]">{r.channel}</span><div className="flex-1 h-2 bg-[#f0ece5] rounded-full overflow-hidden"><div className="h-full bg-[#b8935f]" style={{ width: `${Math.min(100, r.commission / attribution.estimatedCommission * 100)}%` }} /></div><span className="text-[12px] font-medium text-[#2b2b2b] w-20 text-right">{money(r.commission)}</span></div>
            ))}</div>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-[#a8a8a8] italic">Channel benchmarks and attribution are estimates derived from pipeline and industry rates.</p>
    </div>
  );
};

export default AdminGrowthIntel;
